import {
  useMutation,
  useQueryClient,
  UseMutateFunction,
  UseMutateAsyncFunction,
} from "@tanstack/react-query";

import { ShowWithTixologiEvent } from "@punchup/data/legacy/shows";
import { VenuePagesRow } from "@punchup/venues/data/venue_pages";
import { VenueRow } from "@punchup/data/legacy";
import axios from "axios";
import { createShow } from "@punchup/venues/client/components/tixologi-wizard/utils";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useTixologi } from "@punchup/data/legacy/hooks";
import { TixologiResumedEvent } from "@punchup/data/legacy/tixologi";
import { ShowWithComedian } from "@punchup/data/legacy/shows";
import { DuplicateEventData } from "@punchup/data/legacy/venue-manager-shows";

type DuplicateEventParams = {
  show: ShowWithTixologiEvent;
  data?: DuplicateEventData;
};

type DuplicateEventResult = {
  event: TixologiResumedEvent;
  show: ShowWithComedian & { tixologi_event_id: string | null };
};

type UseDuplicateEventReturn = {
  mutate: UseMutateFunction<
    DuplicateEventResult,
    Error,
    DuplicateEventParams,
    unknown
  >;
  mutateAsync: UseMutateAsyncFunction<
    DuplicateEventResult,
    Error,
    DuplicateEventParams,
    unknown
  >;
  isPending: boolean;
  isSuccess: boolean;
  error: Error | null;
};

export function useDuplicateEvent({
  venuePage,
  silent = false,
}: {
  venuePage: VenuePagesRow;
  silent?: boolean;
}): UseDuplicateEventReturn {
  const { tixologiData } = useTixologi({
    id: venuePage.id,
    isComedian: false,
  });
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending, isSuccess, error } = useMutation({
    mutationFn: async ({
      show,
      data,
    }: DuplicateEventParams): Promise<DuplicateEventResult> => {
      if (!show.tixologi_event_id) {
        throw new Error("No Tixologi show ID found");
      }

      const response = await axios.post(
        process.env.NEXT_PUBLIC_TIXOLOGI_API_URL +
          `/punchup/events/${show.tixologi_event_id}/duplicate`,
        {
          start_date: data?.startDate ? data.startDate : null,
          name: data?.name,
          door_time_offset: Number(data?.doorTimeOffset),
          ticket_types: data?.ticketTypes?.map((tt) => ({
            id: tt.id,
            on_sale_datetime: tt.onSaleDateTime,
            off_sale_datetime: tt.offSaleDateTime,
          })),
        },
        { headers: { Authorization: `Bearer ${tixologiData?.token}` } }
      );

      const event = response.data;
      const reconstructedVenueForProducers: Pick<
        VenueRow,
        "id" | "name" | "city" | "state"
      > = {
        id: show.venue_id || "",
        name: show.venue,
        city: show.location.split(",")[0] || "",
        state:
          show.location.split(",").length > 1
            ? show.location.split(",")[1].trim()
            : "",
      };

      const newShow = await createShow(
        event,
        show.show_comedians,
        venuePage,
        reconstructedVenueForProducers
      );

      return { event, show: newShow };
    },
    onSuccess: () => {
      if (!silent) {
        toast({
          title: "Event duplicated successfully",
          variant: "success",
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["showsWithTixologi"],
        refetchType: "active",
      });
    },
    onError: (error) => {
      if (!silent) {
        toast({
          title: "Error duplicating event",
          description: error.message || "Something went wrong",
          variant: "destructive",
        });
      }
    },
  });

  return { mutate, mutateAsync, isPending, isSuccess, error };
}
